import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { FIREBASE_APP, FIREBASE_FIRESTORE } from '../../Firebase';

// Schema types
export interface SchemaField {
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'reference';
  required?: boolean;
  arrayItemType?: SchemaField; // For array types
  objectSchema?: Record<string, SchemaField>; // For object types
  referenceCollection?: string; // For reference types
  description?: string;
}

export interface CollectionSchema {
  fields: Record<string, SchemaField>;
  version: number;
  createdAt?: any;
  updatedAt?: any;
}

// Updated Collection interface
export interface Collection {
  id: string;
  name: string;
  schema?: CollectionSchema;
}

export interface Organization {
  id: string;
  name: string;
  collections?: Collection[];
  roles?: string[];
  createdAt?: any;
  updatedAt?: any;
}

export interface CloudFunctionResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// Initialize Firebase Functions
const functions = getFunctions(FIREBASE_APP, 'europe-west2');

// Cloud Functions
const createOrganizationFn = httpsCallable(functions, 'createOrganization');
const updateOrganizationFn = httpsCallable(functions, 'updateOrganization');
const deleteOrganizationFn = httpsCallable(functions, 'deleteOrganization');
const updateCollectionSchemaFn = httpsCallable(functions, 'updateCollectionSchema');
const getCollectionSchemaFn = httpsCallable(functions, 'getCollectionSchema');
const validateDocumentAgainstSchemaFn = httpsCallable(functions, 'validateDocumentAgainstSchema');

export class DataService {
  // ========== READ OPERATIONS (Direct Firestore) ==========

  /**
   * Get all organizations from Firestore
   */
  static async getOrganizations(): Promise<Organization[]> {
    try {
      console.log('🔥 Firestore READ: collection "organizations" - querying all documents with orderBy(name)');
      const orgsCollection = collection(FIREBASE_FIRESTORE, 'organizations');
      const q = query(orgsCollection, orderBy('name'));
      const querySnapshot = await getDocs(q);
      
      const organizations: Organization[] = [];
      querySnapshot.forEach((doc) => {
        organizations.push({ id: doc.id, ...doc.data() } as Organization);
      });
      
      return organizations;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw new Error('Failed to fetch organizations');
    }
  }

  /**
   * Get a single organization by ID from Firestore
   */
  static async getOrganization(orgId: string): Promise<Organization | null> {
    try {
      console.log(`🔥 Firestore READ: collection "organizations", document "${orgId}"`);
      const orgDoc = doc(FIREBASE_FIRESTORE, 'organizations', orgId);
      const docSnapshot = await getDoc(orgDoc);
      
      if (docSnapshot.exists()) {
        return { id: docSnapshot.id, ...docSnapshot.data() } as Organization;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw new Error('Failed to fetch organization');
    }
  }

  // ========== WRITE OPERATIONS (Cloud Functions) ==========

  /**
   * Create a new organization via Cloud Function
   */
  static async createOrganization(
    id: string,
    name: string,
    collections?: Collection[],
    roles?: string[]
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "createOrganization"', { id, name, collections: collections || [], roles: roles || ['admin', 'read', 'write', 'api'] });
      const result = await createOrganizationFn({
        id,
        name,
        collections: collections || [],
        roles: roles || ['admin', 'read', 'write', 'api']
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create organization');
      }

      return response;
    } catch (error: any) {
      console.error('Error creating organization:', error);
      throw new Error(error.message || 'Failed to create organization');
    }
  }

  /**
   * Update an organization via Cloud Function
   */
  static async updateOrganization(
    orgId: string,
    updates: {
      name?: string;
      collections?: Collection[];
      roles?: string[];
    }
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "updateOrganization"', { orgId, ...updates });
      const result = await updateOrganizationFn({
        orgId,
        ...updates
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update organization');
      }

      return response;
    } catch (error: any) {
      console.error('Error updating organization:', error);
      throw new Error(error.message || 'Failed to update organization');
    }
  }

  /**
   * Delete an organization via Cloud Function
   */
  static async deleteOrganization(orgId: string): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "deleteOrganization"', { orgId });
      const result = await deleteOrganizationFn({ orgId });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete organization');
      }

      return response;
    } catch (error: any) {
      console.error('Error deleting organization:', error);
      throw new Error(error.message || 'Failed to delete organization');
    }
  }

  // ========== SCHEMA OPERATIONS ==========

  /**
   * Update collection schema via Cloud Function
   */
  static async updateCollectionSchema(
    orgId: string,
    collectionId: string,
    schema: CollectionSchema
  ): Promise<CloudFunctionResponse> {
    try {
      console.log('☁️ Cloud Function CALL: "updateCollectionSchema"', { orgId, collectionId, schema });
      const result = await updateCollectionSchemaFn({
        orgId,
        collectionId,
        schema
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update collection schema');
      }

      return response;
    } catch (error: any) {
      console.error('Error updating collection schema:', error);
      throw new Error(error.message || 'Failed to update collection schema');
    }
  }

  /**
   * Get collection schema via Cloud Function
   */
  static async getCollectionSchema(
    orgId: string,
    collectionId: string
  ): Promise<CollectionSchema | null> {
    try {
      console.log('☁️ Cloud Function CALL: "getCollectionSchema"', { orgId, collectionId });
      const result = await getCollectionSchemaFn({
        orgId,
        collectionId
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to get collection schema');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error getting collection schema:', error);
      throw new Error(error.message || 'Failed to get collection schema');
    }
  }

  /**
   * Validate document against schema via Cloud Function
   */
  static async validateDocumentAgainstSchema(
    orgId: string,
    collectionId: string,
    document: any
  ): Promise<ValidationResult> {
    try {
      console.log('☁️ Cloud Function CALL: "validateDocumentAgainstSchema"', { orgId, collectionId, document });
      const result = await validateDocumentAgainstSchemaFn({
        orgId,
        collectionId,
        document
      });

      const response = result.data as CloudFunctionResponse;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to validate document');
      }

      return response.data;
    } catch (error: any) {
      console.error('Error validating document:', error);
      throw new Error(error.message || 'Failed to validate document');
    }
  }

  // ========== SCHEMA HELPER FUNCTIONS ==========

  /**
   * Create a basic schema field
   */
  static createSchemaField(
    type: SchemaField['type'],
    options: Partial<Omit<SchemaField, 'type'>> = {}
  ): SchemaField {
    return {
      type,
      required: options.required || false,
      description: options.description,
      arrayItemType: options.arrayItemType,
      objectSchema: options.objectSchema,
      referenceCollection: options.referenceCollection
    };
  }

  /**
   * Create an array field schema
   */
  static createArrayField(
    itemType: SchemaField,
    required: boolean = false,
    description?: string
  ): SchemaField {
    return {
      type: 'array',
      required,
      description,
      arrayItemType: itemType
    };
  }

  /**
   * Create an object field schema
   */
  static createObjectField(
    objectSchema: Record<string, SchemaField>,
    required: boolean = false,
    description?: string
  ): SchemaField {
    return {
      type: 'object',
      required,
      description,
      objectSchema
    };
  }

  /**
   * Create a reference field schema
   */
  static createReferenceField(
    referenceCollection: string,
    required: boolean = false,
    description?: string
  ): SchemaField {
    return {
      type: 'reference',
      required,
      description,
      referenceCollection
    };
  }

  /**
   * Validate schema field structure (client-side validation)
   */
  static validateSchemaField(field: any): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const validTypes = ['string', 'number', 'boolean', 'date', 'array', 'object', 'reference'];

    if (!field.type || !validTypes.includes(field.type)) {
      errors.push(`Invalid field type. Must be one of: ${validTypes.join(', ')}`);
    }

    if (field.type === 'array' && !field.arrayItemType) {
      errors.push('Array fields must specify arrayItemType');
    }

    if (field.type === 'object' && (!field.objectSchema || typeof field.objectSchema !== 'object')) {
      errors.push('Object fields must specify objectSchema');
    }

    if (field.type === 'reference' && (!field.referenceCollection || typeof field.referenceCollection !== 'string')) {
      errors.push('Reference fields must specify referenceCollection');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // ========== PERMISSION OPERATIONS ==========

  /**
   * Get user permissions from localStorage
   */
  static async getUserPermissions(userEmail: string): Promise<string[]> {
    try {
      const key = `permissions_${userEmail}_permissions`;
      const storedPermissions = localStorage.getItem(key);
      
      if (storedPermissions) {
        const permissions = JSON.parse(storedPermissions);
        return permissions.orgPermissions || [];
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching user permissions from localStorage:', error);
      return [];
    }
  }

  /**
   * Check if user is APP_ADMIN
   */
  static async isAppAdmin(userEmail: string): Promise<boolean> {
    try {
      const key = `permissions_${userEmail}_permissions`;
      const storedPermissions = localStorage.getItem(key);
      
      if (storedPermissions) {
        const permissions = JSON.parse(storedPermissions);
        return permissions.permissions?.includes('APP_ADMIN') || false;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking APP_ADMIN status:', error);
      return false;
    }
  }

  /**
   * Check if user has org-level admin permission
   */
  static async hasOrgAdminPermission(userEmail: string, orgId: string): Promise<boolean> {
    try {
      const permissions = await this.getUserPermissions(userEmail);
      return permissions.includes(`${orgId}_admin`);
    } catch (error) {
      console.error('Error checking org admin permission:', error);
      return false;
    }
  }

  /**
   * Get organization permissions from Firestore
   */
  static async getOrganizationPermissions(orgId: string): Promise<Record<string, string[]> | null> {
    try {
      console.log(`🔥 Firestore READ: collection "org_roles", document "${orgId}"`);
      const orgRolesDoc = doc(FIREBASE_FIRESTORE, 'org_roles', orgId);
      const docSnapshot = await getDoc(orgRolesDoc);
      
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        return data.permissions || {};
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching organization permissions:', error);
      throw new Error('Failed to fetch organization permissions');
    }
  }
}